import React, { useState } from 'react';
import { ExternalLink, Heart, ArrowRight, Play, X, Scissors } from 'lucide-react';
import { Tutorial } from '@/types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBrowser } from '@/contexts/BrowserContext';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Video from '@/components/ui/video';
import { VideoClipEditor } from '@/components/VideoClipEditor';

interface TutorialCardProps {
  tutorial: Tutorial;
  isFavorited?: boolean;
  onFavoriteToggle?: (id: string) => void;
  onClick?: (id: string) => void;
}

export const TutorialCard: React.FC<TutorialCardProps> = ({
  tutorial,
  isFavorited,
  onFavoriteToggle,
  onClick,
}) => {
  const { addTab } = useBrowser();
  const navigate = useNavigate();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isClipEditorOpen, setIsClipEditorOpen] = useState(false);

  // 模拟一些预览视频链接（实际应从数据库获取）
  const previewUrl = (tutorial as any).preview_video_url || "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4";

  const getTagColor = (tag: string) => {
    switch (tag) {
      case '免费':
        return 'bg-accent-free/10 text-accent-free border-accent-free/20';
      case '付费':
        return 'bg-accent-paid/10 text-accent-paid border-accent-paid/20';
      case '开源':
        return 'bg-accent-oss/10 text-accent-oss border-accent-oss/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleCardClick = () => {
    if (onClick) onClick(tutorial.id);
    // 在聚光浏览器中打开目标网站
    navigate(`/juguang-browser?url=${encodeURIComponent(tutorial.link)}`);
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col h-full overflow-hidden rounded-xl border bg-card transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1",
        "border-border/50 hover:border-primary/30"
      )}
    >
      <div className="p-5 flex-1 flex flex-col relative group/content">
        {/* 视频预览悬浮按钮 */}
        <div className="absolute inset-0 bg-primary/40 opacity-0 group-hover/content:opacity-100 transition-all duration-300 flex items-center justify-center z-10 backdrop-blur-[1px] cursor-pointer" onClick={() => setIsPreviewOpen(true)}>
          <div className="bg-white/90 p-3 rounded-full shadow-2xl scale-75 group-hover/content:scale-100 transition-transform duration-300">
            <Play className="h-6 w-6 text-primary fill-current" />
          </div>
          <span className="absolute bottom-6 text-white text-xs font-bold tracking-widest drop-shadow-md">视频预览</span>
        </div>

        <div className="flex justify-between items-start mb-3 relative z-20">
          <Badge variant="outline" className={cn("font-normal shadow-sm", getTagColor(tutorial.tag))}>
            {tutorial.tag}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-8 w-8 rounded-full transition-colors z-20",
              isFavorited ? "text-red-500 bg-red-50 dark:bg-red-900/20" : "text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onFavoriteToggle?.(tutorial.id);
            }}
          >
            <Heart className={cn("h-4 w-4", isFavorited && "fill-current")} />
          </Button>
        </div>

        <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1 relative z-20">
          {tutorial.name}
        </h3>
        
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2 flex-1 relative z-20">
          {tutorial.description}
        </p>

        <div className="flex items-center gap-2 mt-auto relative z-20">
          <Badge variant="secondary" className="font-normal text-[10px] px-1.5 py-0 bg-secondary/50">
            {tutorial.difficulty}
          </Badge>
        </div>
      </div>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden bg-black border-none rounded-2xl shadow-2xl">
          <div className="relative aspect-video">
            <Video 
              src={previewUrl}
              className="w-full h-full object-contain"
              autoPlay
              controls
            />
            <div className="absolute top-4 left-6 z-50">
              <Badge className="bg-primary/90 text-white border-none text-xs px-3 py-1 shadow-lg backdrop-blur-md">
                正在预览: {tutorial.name}
              </Badge>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute top-4 right-4 z-50 text-white/70 hover:text-white hover:bg-white/10 rounded-full"
              onClick={() => setIsPreviewOpen(false)}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="p-6 bg-background">
            <h2 className="text-xl font-bold mb-2 text-foreground">{tutorial.name}</h2>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{tutorial.description}</p>
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Badge variant="secondary" className="bg-primary/5 text-primary border-primary/10">{tutorial.difficulty}</Badge>
                <Badge variant="outline">{tutorial.tag}</Badge>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  className="rounded-xl px-4 font-bold border-primary/20 hover:bg-primary/5"
                  onClick={() => {
                    setIsPreviewOpen(false);
                    setIsClipEditorOpen(true);
                  }}
                >
                  <Scissors className="h-4 w-4 mr-2" />
                  剪辑片段
                </Button>
                <Button 
                  className="bg-primary text-white hover:bg-primary/90 rounded-xl px-6 font-bold shadow-glow-sm"
                  onClick={() => {
                    setIsPreviewOpen(false);
                    handleCardClick();
                  }}
                >
                  前往完整教程
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 视频剪辑编辑器 */}
      <VideoClipEditor
        videoUrl={previewUrl}
        tutorialId={tutorial.id}
        tutorialName={tutorial.name}
        isOpen={isClipEditorOpen}
        onClose={() => setIsClipEditorOpen(false)}
      />

      <div 
        className="px-5 py-3 border-t border-border/50 bg-muted/30 flex items-center justify-between cursor-pointer group/btn hover:bg-primary/5 transition-colors"
        onClick={handleCardClick}
      >
        <span className="text-xs font-medium text-muted-foreground group-hover/btn:text-primary flex items-center gap-1">
          前往官网 <ExternalLink className="h-3 w-3" />
        </span>
        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover/btn:text-primary transition-transform group-hover/btn:translate-x-1" />
      </div>
    </div>
  );
};
已